import { useState } from 'react';
import { Plus, X, Check } from 'lucide-react';
import { useEmployees } from '@/api/queries/employee.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/formatters';

const MOCK_ADVANCES = [
  { id: 'adv-001', employee: { id: 'emp-002', name: 'Arjun Patel' }, amount: 20000, reason: 'Medical emergency', requested_on: '2026-06-01', status: 'APPROVED', repayment_months: 2 },
  { id: 'adv-002', employee: { id: 'emp-005', name: 'Rahul Singh' }, amount: 15000, reason: 'Home repair', requested_on: '2026-06-05', status: 'PENDING', repayment_months: 3 },
];

function AddAdvanceModal({ employees, onClose }) {
  const [form, setForm] = useState({ employee_id: '', amount: '', reason: '', repayment_months: '3' });
  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">New Advance</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Employee</label>
            <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} className={inputCls}>
              <option value="">Select employee</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Amount (₹)</label>
            <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="10000" className={inputCls}/>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Reason</label>
            <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Medical, education, etc." className={inputCls}/>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Repay over (months)</label>
            <select value={form.repayment_months} onChange={e => setForm(f => ({ ...f, repayment_months: e.target.value }))} className={inputCls}>
              {[1,2,3,6].map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={onClose}>Submit Request</Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdvancesPage() {
  const { data: empResp } = useEmployees();
  const employees = empResp?.data || [];
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Salary Advances"
        subtitle="Manage advance salary requests"
        breadcrumb={[{ label: 'Payroll', href: '/payroll' }, { label: 'Advances' }]}
        actions={<Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>New Advance</Button>}
      />

      <div className="card p-0 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[var(--border)]">
            {['Employee','Amount','Reason','Repayment','Requested','Status',''].map(h => (
              <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {MOCK_ADVANCES.map(a => (
              <tr key={a.id} className="hover:bg-[var(--bg-muted)]">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <Avatar name={a.employee.name} size="sm"/>
                    <p className="text-[13px] font-semibold text-[var(--text-primary)]">{a.employee.name}</p>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-[13px] font-bold text-[var(--text-primary)]">{formatCurrency(a.amount)}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{a.reason}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{a.repayment_months} month{a.repayment_months > 1 ? 's' : ''} · {formatCurrency(a.amount / a.repayment_months)}/mo</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{a.requested_on}</td>
                <td className="px-5 py-3.5"><StatusBadge status={a.status}/></td>
                <td className="px-5 py-3.5">
                  {a.status === 'PENDING' && (
                    <div className="flex gap-2">
                      <button className="flex items-center gap-1 text-[11px] font-semibold text-green-600 hover:underline"><Check size={11}/> Approve</button>
                      <button className="flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"><X size={11}/> Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[12px] text-[var(--text-tertiary)]">Approved advances are automatically deducted from the next payroll run.</p>
      {showAdd && <AddAdvanceModal employees={employees} onClose={() => setShowAdd(false)}/>}
    </div>
  );
}
